import * as React from "react";
import { styled } from "@mui/material/styles";
import { Button } from "@mui/material";
import usePagination from "@mui/material/usePagination";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
/* ---------- Styled Components ---------- */

const List = styled("ul")({
  listStyle: "none",
  padding: 0,
  margin: 0,
  display: "flex",
  alignItems: "center",
  width: "100%",
});

const lightThemeColor = JSON.parse(
  localStorage.getItem("lightThemeColor") || "{}"
);

const setBRadius = lightThemeColor.borderRadius || "4px";

const PageButton = styled(Button)<{ selected?: boolean }>(
  ({ theme, selected }) => ({
    minWidth: "42px",
    height: "33px",
    padding: 0,
    boxShadow: "none",
    textTransform: "none",
    backgroundColor: selected
      ? theme.palette.primary.main
      : theme.palette.background.paper,
    color: selected
      ? theme.palette.cardAdd.secBackColor
      : theme.palette.text.primary,
    "&:hover": {
      backgroundColor: selected
        ? theme.palette.primary.dark
        : theme.palette.action.hover,
    },
    borderRadius: setBRadius,
  })
);

const ListItem = styled("li")({
  margin: 0,
  padding: 2,
  display: "flex",
  alignItems: "center",
});

/* ---------- Pagination Component ---------- */

const Pagination: React.FC<{
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: React.MouseEvent<HTMLElement>, page: number) => void;
}> = ({ count, page, rowsPerPage, onPageChange }) => {
  const { items } = usePagination({
    count: Math.ceil(count / rowsPerPage),
    page: page + 1,
  });

  return (
    <nav>
      <List>
        {items.map(({ page: itemPage, type, selected, ...item }, index) => {
          const handleClick = (event: React.MouseEvent<HTMLElement>) => {
            if (itemPage !== null) {
              onPageChange(event, itemPage - 1);
            }
          };

          const { onClick, ...itemProps } = item;

          let children: React.ReactNode = null;

          /* ---------- Ellipsis ---------- */
          if (type === "start-ellipsis" || type === "end-ellipsis") {
            children = <span>…</span>;
          } else if (type === "page") {

          /* ---------- Page Numbers ---------- */
            children = (
              <PageButton
                type="button"
                onClick={handleClick}
                selected={selected}
                {...itemProps}
              >
                {itemPage}
              </PageButton>
            );
          } else {

          /* ---------- Previous / Next Icons ---------- */
            children = (
              <Button
                type="button"
                onClick={handleClick}
                {...itemProps}
                sx={{
                  minWidth: "42px",
                  height: "33px",
                  padding: 0,
                  borderRadius: setBRadius,
                  ":disabled": {
                    color: "#9e9e9e",
                  },
                }}
              >
                {type === "previous" ? (
                  <KeyboardDoubleArrowLeftIcon fontSize="small" />
                ) : (
                  <KeyboardDoubleArrowRightIcon fontSize="small" />
                )}
              </Button>
            );
          }

          return <ListItem key={index}>{children}</ListItem>;
        })}
      </List>
    </nav>
  );
};

export default Pagination;
